import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { RootState } from "../../store"
import {
  API_STATUS,
  DATE_FORMAT,
  MONTHS_DISPLAY_MAPPER,
} from "../../util/StaticData"
import AxiosOperations from "../../services/AxiosOperations"
import { API } from "../../util/api"
import {
  IMaintenanceExecutionData,
  IMonthlyStaticOption,
  INewTableCellChangePayload,
} from "../../util/interface"
import {
  differenceInMilliseconds,
  format,
  formatDuration,
  intervalToDuration,
} from "date-fns"
import { usePapaParse } from "react-papaparse"
import {
  parseAndFormatDate,
  pipelineConfigMapping,
  sortByDate,
  sortByYearAndWeek,
} from "../../util/CommonMethods"
import { newPipeLineMapper } from "../../util/NewPipelineConfig"

const defaultScheduleRun: any = {
  timeZone: "",
  time: null,
  frequency: "",
  recurrencePattern: [],
  rangeOfRecurrence: {
    start: null,
    end: null,
  },
}
const pipelineConfig: any = {
  tabIndex: "RAW Config & Quality Config",
  tableData: {
    "RAW Config & Quality Config": [],
    "UDM Config": [],
    "ADM Config": [],
    "RDQ Config": [],
    "Input Series Config": [],
    "RAW Data Drift Config": [],
    "Flexible Schema Config": [],
    "Model Config": [],
  },
  activeData: [],
  status: API_STATUS.IDLE,
  userChangedTabs: [],
}
const monthlySelectedDays: IMonthlyStaticOption[] = []
const chartData: any = {}
const viewConfigData: any = {}
const activeRunDetails: any = {}
const initialState = {
  status: API_STATUS.IDLE,
  isModalOpen: false,
  isScheduleModelOpen: false,
  isRunModelOpen: false,
  isRunModalChartActive: false,
  isDefaultConfigModelOpen: false,
  defaultScheduleRun,
  executionHistoryStatus: API_STATUS.IDLE,
  executionHistoryData: [],
  chartData,
  chartStatus: API_STATUS.IDLE,
  chartView: "week",
  sideView: false,
  maintenanceListData: [],
  maintenanceListStatus: API_STATUS.IDLE,
  pipelineConfig,
  activeRunDetails,
  viewConfigData,
  monthlySelectedDays,
  schedulePipelineStatus: API_STATUS.IDLE,
  configBaseDataStatus: API_STATUS.IDLE,
  lastModifiedDate: "",
}

export const maintenanceListAsync = createAsyncThunk(
  "maintenance/list",
  async (_, { getState }) => {
    try {
      const state: any = getState()
      // const { startFormattedDate, endFormattedDate } =
      //   extractAndFormatDate(state)
      const axiosObj = new AxiosOperations()
      const payload = {
        use_case: state.filters.useCaseSelectedValue,
        market_place: state.filters.marketSingleCaseSelectedValue,
        // start_date: startFormattedDate,
        // end_date: endFormattedDate,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      }
      // if (!startFormattedDate || !endFormattedDate) {
      //   delete payload.start_date
      //   delete payload.end_date
      // }
      return axiosObj.get(API.MAINTENANCE_LIST, payload)
    } catch (error) {
      console.error("Error:", error)
    }
  },
)

export const maintenanceLastModifiedDate = createAsyncThunk(
  "maintenance/last-modified-date",
  async (_, { getState }) => {
    try {
      const axiosObj = new AxiosOperations()
      const state: any = getState()
      const payload = {
        market_place: state.filters.marketSingleCaseSelectedValue,
        usecase: state.filters.useCaseSelectedValue,
      }
      return axiosObj.get(API.MAINTENANCE_LAST_MODIFIED_DATE, payload)
    } catch (error) {
      console.error("Error:", error)
    }
  },
)

export const maintenanceExecutionHistoryAsync = createAsyncThunk(
  "maintenance/execution-history",
  async (_, { getState }) => {
    try {
      const state: any = getState()
      const { startFormattedDate, endFormattedDate } =
        extractAndFormatDate(state)
      const axiosObj = new AxiosOperations()
      const payload = {
        pipeline_id: state.maintenance.activeRunDetails.pipelineId,
        start_date: startFormattedDate,
        end_date: endFormattedDate,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      }
      if (!startFormattedDate || !endFormattedDate) {
        delete payload.start_date
        delete payload.end_date
      }
      return axiosObj.get(API.MAINTENANCE_EXECUTION_HISTORY, payload)
    } catch (error) {
      console.error("Error:", error)
    }
  },
)

export const maintenanceChartAsync = createAsyncThunk(
  "maintenance/chart",
  async (_, { getState }) => {
    try {
      const state: any = getState()
      const axiosObj = new AxiosOperations()
      const payload = {
        view_by: state.maintenance.chartView,
        pipeline_id: state.maintenance.activeRunDetails.pipelineId,
      }
      return axiosObj.get(API.MAINTENANCE_CHART, payload)
    } catch (error) {
      console.error("Error:", error)
    }
  },
)
export const maintenancePipeLineBaseDataAsync = createAsyncThunk(
  "maintenance/new-pipeline-base-data",
  async (_, { getState }) => {
    try {
      const state: any = getState()
      const axiosObj = new AxiosOperations()
      const payload = {
        config_type:
          newPipeLineMapper[state.maintenance.pipelineConfig.tabIndex],
        usecase: state.filters.useCaseSelectedValue,
        market_place: state.filters.marketSingleCaseSelectedValue,
      }
      return axiosObj.get(API.NEW_PIPELINE_CONFIG, payload)
    } catch (error) {
      console.error("Error:", error)
    }
  },
)
export const maintenanceViewPipeLineBaseDataAsync = createAsyncThunk(
  "maintenance/view-pipeline-base-data",
  async ({ tab, runId }: { tab: string; runId: string }, { getState }) => {
    try {
      const state: any = getState()
      const axiosObj = new AxiosOperations()
      const payload = {
        config_type: newPipeLineMapper[tab],
        usecase: state.filters.useCaseSelectedValue,
        run_id: runId,
        market_place: state.filters.marketSingleCaseSelectedValue,
      }
      return axiosObj.get(API.MAINTENANCE_GET_DEFAULT_CONFIG, payload)
    } catch (error) {
      console.error("Error:", error)
    }
  },
)

export const maintenancePipeLineUpdateBaseDataAsync = createAsyncThunk(
  "maintenance/new-pipeline-update-base-data",
  async ({ data, fileType }: any, { getState }) => {
    try {
      const { jsonToCSV } = usePapaParse()

      const state: any = getState()
      const axiosObj = new AxiosOperations()
      const csv = jsonToCSV(data)
      const payload = {
        data: csv,
        file_type: fileType,
        usecase: state.filters.useCaseSelectedValue,
      }
      return axiosObj.post(API.UPDATE_NEWPIPELINE_CONFIG, payload)
    } catch (error) {
      console.error("Error:", error)
    }
  },
)

export const maintenanceScheduleDefaultRunAsync = createAsyncThunk(
  "maintenance/schedule-default-run",
  async (_, { getState }) => {
    try {
      const state: any = getState()
      const axiosObj = new AxiosOperations()
      const frequency = state.maintenance.defaultScheduleRun.frequency
      const payload: any = {
        pipeline_name: state.maintenance.activeRunDetails.pipelineName,
        frequency,
        interval: 1,
        start_date: format(
          new Date(
            state.maintenance.defaultScheduleRun.rangeOfRecurrence.start,
          ),
          "yyyy-MM-dd",
        ),
        end_date: format(
          new Date(state.maintenance.defaultScheduleRun.rangeOfRecurrence.end),
          "yyyy-MM-dd",
        ),
        time_zone: state.maintenance.defaultScheduleRun.timeZone,
        minutes: [
          new Date(state.maintenance.defaultScheduleRun.time).getMinutes(),
        ],
        hours: [new Date(state.maintenance.defaultScheduleRun.time).getHours()],
        week_days: state.maintenance.defaultScheduleRun.recurrencePattern,
      }
      if (frequency === "day" || frequency === "month") {
        delete payload.week_days
      }
      if (frequency === "month") {
        payload.month_days = state.maintenance.monthlySelectedDays.map(
          (obj: IMonthlyStaticOption) => obj.value,
        )
      }
      return axiosObj.post(API.SCHEDULE_DEFAULT_RUN, payload)
    } catch (error) {
      console.error("Error:", error)
    }
  },
)

export const MaintenanceSlice = createSlice({
  name: "Maintenance",
  initialState,
  reducers: {
    timeZoneChange: (state, action: PayloadAction<string>) => {
      state.defaultScheduleRun.timeZone = action.payload
    },
    timeChange: (state, action: PayloadAction<string | null>) => {
      state.defaultScheduleRun.time = action.payload
    },
    frequencyChange: (state, action: PayloadAction<string>) => {
      state.defaultScheduleRun.frequency = action.payload
    },
    startDateChange: (state, action: PayloadAction<any>) => {
      state.defaultScheduleRun.rangeOfRecurrence.start = action.payload
    },
    endDateChange: (state, action: PayloadAction<any>) => {
      state.defaultScheduleRun.rangeOfRecurrence.end = action.payload
    },
    chartViewChange: (state, action: PayloadAction<any>) => {
      state.chartView = action.payload
    },
    recurrencePatternChange: (state, action: PayloadAction<any>) => {
      state.defaultScheduleRun.recurrencePattern = action.payload
    },
    setActiveRunDetails: (state, action: PayloadAction<any>) => {
      state.activeRunDetails = action.payload
    },
    updateViewConfigData: (state, action: PayloadAction<any>) => {
      state.viewConfigData[action.payload.key] = action.payload.data
    },
    resetViewConfigData: (state) => {
      state.viewConfigData = {}
    },
    resetMaintenanceState: () => {
      return structuredClone(initialState)
    },
    openDialog: (state) => {
      state.isModalOpen = true
    },
    closeDialog: (state) => {
      state.isModalOpen = false
    },
    openScheduleDialog: (state) => {
      state.isScheduleModelOpen = true
    },
    closeScheduleDialog: (state) => {
      state.isScheduleModelOpen = false
    },
    openRunDialog: (state) => {
      state.isRunModelOpen = true
    },
    closeRunDialog: (state) => {
      state.isRunModelOpen = false
    },
    openDefaultConfigDialog: (state) => {
      state.isDefaultConfigModelOpen = true
    },
    closeDefaultConfigDialog: (state) => {
      state.isDefaultConfigModelOpen = false
    },
    changeView: (state, action: PayloadAction<any>) => {
      state.sideView = action.payload
    },
    updateMonthlySelectedDays: (
      state,
      action: PayloadAction<IMonthlyStaticOption>,
    ) => {
      state.monthlySelectedDays = [...state.monthlySelectedDays, action.payload]
    },
    removeMonthlySelectedDays: (
      state,
      action: PayloadAction<IMonthlyStaticOption>,
    ) => {
      state.monthlySelectedDays = state.monthlySelectedDays.filter(
        (day) => day.value !== action.payload.value,
      )
    },
    resetMonthlySelectedDays: (state) => {
      state.monthlySelectedDays = []
    },
    resetUserChangedTabs: (state) => {
      state.pipelineConfig.userChangedTabs = []
    },
    newPipelineCellChange: (
      state,
      action: PayloadAction<INewTableCellChangePayload>,
    ) => {
      const temp = action.payload
      state.pipelineConfig.userChangedTabs.push(state.pipelineConfig.tabIndex)
      state.pipelineConfig.activeData[temp.rowIndex][temp.keyName] = temp.value
      state.pipelineConfig.tableData[state.pipelineConfig.tabIndex] = [
        ...state.pipelineConfig.activeData,
      ]
    },
    updateTabIndex: (state, action: PayloadAction<string>) => {
      state.pipelineConfig.tabIndex = action.payload
      state.pipelineConfig.activeData =
        state.pipelineConfig.tableData[state.pipelineConfig.tabIndex]
    },
    resetDefaultScheduleRunDetails: (state) => {
      state.defaultScheduleRun = defaultScheduleRun
    },
    setRunModalChartActive: (state) => {
      state.isRunModalChartActive = !state.isRunModalChartActive
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(maintenanceExecutionHistoryAsync.pending, (state) => {
        state.executionHistoryStatus = API_STATUS.LOADING
      })
      .addCase(maintenanceExecutionHistoryAsync.fulfilled, (state, action) => {
        try {
          state.executionHistoryStatus = API_STATUS.IDLE
          state.executionHistoryData = action.payload.map((obj: any) => {
            const difference = differenceInMilliseconds(
              new Date(obj.end_time),
              new Date(obj.start_time),
            )
            const duration = intervalToDuration({ start: 0, end: difference })
            const formattedDuration = formatDuration(duration, {
              delimiter: " ",
              zero: false,
            })
            const executionFormattedDate = format(
              new Date(obj.start_time),
              "dd/MM/yyyy, hh:mm:ss a",
            )
            const tempObj: IMaintenanceExecutionData = {
              pipelineRunIds: obj.az_pipeline_run_id,
              executionDate: executionFormattedDate,
              duration: formattedDuration,
              status: obj.execution_status,
              start_time: obj.start_time,
              end_time: obj.end_time,
            }
            return tempObj
          })
        } catch (error) {
          console.error(error)
        }
      })
      .addCase(maintenanceExecutionHistoryAsync.rejected, (state) => {
        state.executionHistoryStatus = API_STATUS.FAILED
      })
      .addCase(maintenanceChartAsync.pending, (state) => {
        state.chartStatus = API_STATUS.LOADING
      })
      .addCase(maintenanceChartAsync.fulfilled, (state, action) => {
        try {
          let categories
          if (state.chartView === "week") {
            action.payload = sortByYearAndWeek(
              action.payload,
              "year",
              "week_number",
            )
            categories = action.payload.map(
              (obj: any) => `W${obj.week_number}-${obj.year}`,
            )
          } else if (state.chartView === "month") {
            action.payload = sortByYearAndWeek(action.payload, "year", "month")
            categories = action.payload.map(
              (obj: any) =>
                `${Object.keys(MONTHS_DISPLAY_MAPPER)[obj.month - 1]}-${
                  obj.year
                }`,
            )
          } else {
            action.payload = sortByDate(action.payload)
            categories = action.payload.map((obj: any) => obj.start_time__date)
          }
          action.payload = action.payload.sort(
            (a: any, b: any) => parseFloat(a.year) - parseFloat(b.year),
          )

          state.chartData.categories = categories
          const data = action.payload.map((obj: any) => {
            return {
              y: obj.total_count,
              custom: {
                passed: obj.passed_count,
                failed: obj.failed_count,
              },
            }
          })
          state.chartData.totalCount = data
          state.chartStatus = API_STATUS.IDLE
        } catch (error) {
          console.error(error)
        }
      })
      .addCase(maintenanceChartAsync.rejected, (state) => {
        state.chartStatus = API_STATUS.FAILED
      })
      .addCase(maintenanceListAsync.pending, (state) => {
        state.maintenanceListStatus = API_STATUS.LOADING
      })
      .addCase(maintenanceListAsync.fulfilled, (state, action) => {
        try {
          state.maintenanceListStatus = API_STATUS.IDLE
          state.maintenanceListData = action.payload.map((obj: any) => {
            const difference = differenceInMilliseconds(
              new Date(obj.end_time),
              new Date(obj.start_time),
            )
            const duration = intervalToDuration({ start: 0, end: difference })
            const formattedDuration = formatDuration(duration, {
              delimiter: " ",
              zero: false,
            })
            return {
              lastExecuted: format(
                new Date(obj.end_time),
                "dd/MM/yyyy, hh:mm:ss a",
              ),
              duration: formattedDuration,
              usecase: obj.use_case,
              market: obj.market_place,
              pipelineName: obj.Pipeline_name,
              status: obj.execution_status,
              pipelineId: obj.pipeline_id,
            }
          })
        } catch (error) {
          console.error(error)
        }
      })
      .addCase(maintenanceListAsync.rejected, (state) => {
        state.maintenanceListStatus = API_STATUS.FAILED
      })
      .addCase(maintenancePipeLineBaseDataAsync.pending, (state) => {
        state.pipelineConfig.status = API_STATUS.LOADING
      })
      .addCase(
        maintenancePipeLineBaseDataAsync.fulfilled,
        (state, action: any) => {
          try {
            state.pipelineConfig.status = API_STATUS.IDLE
            let tableData = JSON.parse(action["payload"]["data_csv"])

            tableData = pipelineConfigMapping(
              tableData,
              state.pipelineConfig.tabIndex,
            )
            state.pipelineConfig.tableData[state.pipelineConfig.tabIndex] =
              tableData
            state.pipelineConfig.activeData =
              state.pipelineConfig.tableData[state.pipelineConfig.tabIndex]
          } catch (error) {
            state.pipelineConfig.status = API_STATUS.FAILED
          }
        },
      )
      .addCase(maintenancePipeLineBaseDataAsync.rejected, (state) => {
        state.pipelineConfig.status = API_STATUS.FAILED
      })
      .addCase(maintenanceViewPipeLineBaseDataAsync.pending, (state) => {
        state.pipelineConfig.status = API_STATUS.LOADING
      })
      .addCase(maintenanceViewPipeLineBaseDataAsync.fulfilled, (state) => {
        state.pipelineConfig.status = API_STATUS.IDLE
      })
      .addCase(maintenanceViewPipeLineBaseDataAsync.rejected, (state) => {
        state.pipelineConfig.status = API_STATUS.FAILED
      })
      .addCase(maintenanceScheduleDefaultRunAsync.pending, (state) => {
        state.schedulePipelineStatus = API_STATUS.LOADING
      })
      .addCase(maintenanceScheduleDefaultRunAsync.fulfilled, (state) => {
        state.schedulePipelineStatus = API_STATUS.IDLE
      })
      .addCase(maintenanceScheduleDefaultRunAsync.rejected, (state) => {
        state.schedulePipelineStatus = API_STATUS.FAILED
      })
      .addCase(maintenancePipeLineUpdateBaseDataAsync.pending, (state) => {
        state.configBaseDataStatus = API_STATUS.LOADING
      })
      .addCase(maintenancePipeLineUpdateBaseDataAsync.fulfilled, (state) => {
        state.configBaseDataStatus = API_STATUS.IDLE
      })
      .addCase(maintenancePipeLineUpdateBaseDataAsync.rejected, (state) => {
        state.configBaseDataStatus = API_STATUS.FAILED
      })
      .addCase(maintenanceLastModifiedDate.fulfilled, (state, action: any) => {
        state.lastModifiedDate = format(
          new Date(action.payload.latest_modified_date),
          "dd/MM/yyyy, hh:mm:ss a",
        )
      })
  },
})
export const tabDataSelector = (state: RootState) => state.dataMetaData.tabs
export default MaintenanceSlice.reducer

export const {
  openDialog,
  closeDialog,
  openScheduleDialog,
  closeScheduleDialog,
  closeRunDialog,
  openRunDialog,
  openDefaultConfigDialog,
  closeDefaultConfigDialog,
  endDateChange,
  startDateChange,
  frequencyChange,
  timeChange,
  timeZoneChange,
  recurrencePatternChange,
  chartViewChange,
  changeView,
  newPipelineCellChange,
  updateTabIndex,
  resetDefaultScheduleRunDetails,
  setActiveRunDetails,
  updateViewConfigData,
  resetViewConfigData,
  removeMonthlySelectedDays,
  updateMonthlySelectedDays,
  resetMonthlySelectedDays,
  resetUserChangedTabs,
  resetMaintenanceState,
  setRunModalChartActive,
} = MaintenanceSlice.actions
function extractAndFormatDate(state: any) {
  let startFormattedDate
  let endFormattedDate
  if (state.filters.startDate && state.filters.endDate) {
    startFormattedDate = parseAndFormatDate(
      state.filters.startDate,
      DATE_FORMAT,
      "yyyy-MM-dd",
    )
    endFormattedDate = parseAndFormatDate(
      state.filters.endDate,
      DATE_FORMAT,
      "yyyy-MM-dd",
    )
  }
  return {
    startFormattedDate,
    endFormattedDate,
  }
}
